import LandlordModel from './Landlord';
import RenterModel from './Renter';
import NextPayableModel from './NextPayable';
import NextScheduleMonthModel from './NextScheduleMonth';
import PaymentDetailsModel from './PaymentDetails';
import PaymentHistoryModel from './PaymentHistory';
import PaymentMethodsModel from './PaymentMethods';
import PaymentMethodTypesModel from './PaymentMethodTypes';
import { MoveInCostsDetailsModel } from './MoveInCosts';
import RentTermsModel from './RentTerms';
import DismissibleActionStatus from '@common/models/DismissibleActionStatus';
import DashboardSubheading from './DashboardSubheading';
import OnboardingStatus from '@renter-hub/constants/onboardingStatus';
import TransactionConstants from '@common/constants/TransactionConstants';
import type { RenterPaymentHistoryInterface, RenterStatusNumericInputInterface } from './Types';
import ONE_TIME_PAYMENT_TYPE from '@common/constants/oneTimePaymentTypes';

const getUnpaidOtpIndexes = (paymentHistory: Array<RenterPaymentHistoryInterface>): Array<string> => {
  // used to validate showing pay one time payment screen
  if (!paymentHistory[0] || !paymentHistory[0].transactions) {
    return [];
  }
  const unpaidFailedRegex = `${TransactionConstants.STATUS.UNPAID}|${TransactionConstants.STATUS.PAYMENT_FAILED}`;
  return paymentHistory[0].transactions
    .filter((transaction) => transaction.paymentCategory === TransactionConstants.CATEGORY.ONE_TIME_PAYMENT)
    .filter((transaction) => transaction.renterStatus.match(new RegExp(unpaidFailedRegex)))
    .map((transaction) =>
      transaction.paymentType === ONE_TIME_PAYMENT_TYPE.PAYMENT_PLAN ? transaction.requestKey : transaction.groupKey,
    );
};

const getPastDueRentPaymentIndexes = (paymentHistory: Array<RenterPaymentHistoryInterface>): Array<string> => {
  // used to validate showing pay one time payment screen
  if (!paymentHistory[0] || !paymentHistory[0].transactions) {
    return [];
  }
  const unpaidFailedRegex = `${TransactionConstants.STATUS.PAST_DUE}|${TransactionConstants.STATUS.PAYMENT_FAILED}`;
  return paymentHistory[0].transactions
    .filter((transaction) => transaction.paymentCategory === TransactionConstants.CATEGORY.RENT_PAYMENT)
    .filter((transaction) => transaction.renterStatus.match(new RegExp(unpaidFailedRegex)))
    .map((transaction) => transaction.scheduleMonth);
};

const RenterStatusModel = (data?: RenterStatusNumericInputInterface) => {
  const {
    bankAccountVerificationNeeded = false,
    landlordProfile = {
      name: '',
      email: '',
    },
    renterProfile = {
      name: '',
      email: '',
    },
    nextPayable,
    nextScheduleMonth,
    moveInCostsDetails = {
      baseAmount: 0,
      dueDate: 0,
      groupKey: '',
      isScheduled: false,
      items: [],
      status: '',
      totalAmount: 0,
      zgFee: 0,
      sendDate: 0,
    },
    currentScheduleMonth = {},
    onboardingBrand = 'ZILLOW',
    onboardingStatus = OnboardingStatus.NOT_STARTED,
    onboardingComplete = false,
    isColoradoCustomer = false,
    paymentMethods = [
      {
        accountLast4: '',
        accountType: '',
        displayName: '',
        id: '',
        nickname: '',
        rentAmountDetails: { fee: 0, rentAmount: 0, totalAmount: 0 },
        oneTimePaymentAmountDetails: [],
        createdAt: '',
      },
    ],
    paymentMethodTypes = [
      {
        name: '',
        type: '',
        rentFee: '',
        otherFee: '',
      },
    ],
    paymentDetails = {
      paymentMethodStatus: '',
      accountLast4: '',
      accountName: '',
      accountType: '',
      createdThrough: '',
      paymentSubscriptionDetails: {
        amount: 0,
        firstScheduledDate: 0,
        nextArrivalDate: 0,
        nextScheduledDate: 0,
        recurring: false,
        paymentMethodId: '',
      },
      accountInfo: {
        accountName: '',
        accountType: '',
        accountLast4: '',
        createdThrough: '',
      },
    },
    paymentHistory = [
      {
        propertyId: '',
        transactions: [],
        lateRentPayments: [],
      },
    ],
    rentTerms = {
      address: '',
      amount: 0,
      firstDueDate: 0,
      monthToMonth: false,
      numberOfPayments: 0,
      rentAmountChangeRequest: {},
      rentTermsKey: '',
      addressInfo: {},
    },
    totalAmountForCreditCard = null,
    dismissibleActions = {},
    rentPaymentSubheading = {
      title: '',
      statement: [],
    },
    moveInCostsSubheading = {
      title: '',
      statement: [],
    },
    config = {
      stripePublicKey: '',
      plaidEnv: '',
    },
    enableCreditTracking = false,
  } = data || {};

  return {
    ...data,
    bankAccountVerificationNeeded,
    isColoradoCustomer,
    landlordProfile: LandlordModel(landlordProfile),
    renterProfile: RenterModel(renterProfile),
    nextPayable: (nextPayable && NextPayableModel(nextPayable)) || {},
    nextScheduleMonth: (nextScheduleMonth && NextScheduleMonthModel(nextScheduleMonth)) || {},
    moveInCostsDetails: moveInCostsDetails && MoveInCostsDetailsModel(moveInCostsDetails),
    currentScheduleMonth,
    onboardingBrand,
    onboardingStatus,
    onboardingComplete,
    paymentDetails: PaymentDetailsModel(paymentDetails),
    paymentHistory: PaymentHistoryModel(paymentHistory),
    paymentMethods: PaymentMethodsModel(paymentMethods),
    paymentMethodTypes: PaymentMethodTypesModel(paymentMethodTypes),
    rentTerms: RentTermsModel(rentTerms),
    totalAmountForCreditCard: totalAmountForCreditCard && Number(totalAmountForCreditCard),
    unpaidOtpIndexes: getUnpaidOtpIndexes(paymentHistory),
    pastDueRentPaymentIndexes: getPastDueRentPaymentIndexes(paymentHistory),
    dismissibleActions: DismissibleActionStatus(dismissibleActions),
    rentPaymentSubheading: DashboardSubheading(rentPaymentSubheading),
    moveInCostsSubheading: DashboardSubheading(moveInCostsSubheading),
    config,
    enableCreditTracking,
  };
};

export default RenterStatusModel;
